import type { CamposDoCliente } from '../componentes/FormularioCliente.tsx';
import type { Prospeccao, SituacaoDaProspeccao } from './api.ts';

/**
 * O vocabulário do funil, num lugar só — 20/09/2026.
 *
 * ⚠️ A lista e a ficha da empresa pintam as mesmas etapas, os mesmos grupos e a
 * mesma régua de tempo. Copiado nos dois arquivos, divergiria na primeira
 * correção feita em um deles, que é a lição que esta casa já escreveu três
 * vezes (`voz-do-prompt.ts`, `regrasDaCasa`).
 */

export const ETAPAS: Array<{ id: SituacaoDaProspeccao; nome: string; ajuda: string }> = [
  { id: 'a_contatar', nome: 'A contatar', ajuda: 'Está na lista, nada foi feito.' },
  { id: 'contatado', nome: 'Contatado', ajuda: 'O e-mail saiu.' },
  { id: 'retomada', nome: 'Retomada', ajuda: 'A retomada saiu.' },
  /* ⭐ PRD §63.3 (22/10/2026): a caixa leva a empresa para cá sozinha, na hora em que ela responde. */
  { id: 'respondeu_aguardando', nome: 'Respondeu, aguardando nós', ajuda: 'Respondeu, e a próxima mensagem é nossa. As retomadas pararam sozinhas.' },
  { id: 'conversando', nome: 'Conversando', ajuda: 'Respondeu, e a conversa está em pé.' },
  { id: 'reuniao_marcada', nome: 'Reunião marcada', ajuda: 'Há reunião no calendário.' },
  { id: 'proposta_enviada', nome: 'Proposta enviada', ajuda: 'A proposta foi enviada.' },
  { id: 'fechado', nome: 'Fechado', ajuda: 'Virou cliente.' },
  { id: 'sem_resposta', nome: 'Sem resposta', ajuda: 'A sequência de e-mails terminou sem resposta.' },
  { id: 'recusou', nome: 'Recusou', ajuda: 'Disse não.' },
];

export const GRUPOS: Record<string, string> = {
  'A-escritorio': 'Escritórios',
  'B-fundo': 'Fundos',
  'B-servicer': 'Servicers',
  'B-fomento': 'Fomento',
  'C-credor': 'Credores',
  'D-parceiro': 'Parceiros',
};

export const nomeDaEtapa = (s: SituacaoDaProspeccao) => ETAPAS.find((e) => e.id === s)?.nome ?? s;

/** ⭐ PRD §63.2: o motivo do "insucesso", em palavras. */
export const MOTIVO_DO_INSUCESSO: Record<string, string> = {
  sem_email_valido: 'sem e-mail válido',
  dominio_nao_recebe: 'o domínio não recebe e-mail',
  dominio_bloqueado: 'o domínio recusa os nossos e-mails',
};

/** ⭐ PRD §63.2: o tipo do e-mail que voltou, em palavras. */
export const NOME_DO_RETORNO: Record<string, string> = {
  endereco_nao_existe: 'endereço não existe',
  dominio_nao_recebe: 'domínio não recebe',
  bloqueio: 'bloqueio do servidor',
  passageiro: 'falha passageira',
  nao_sei: 'tipo não identificado',
};

/** As iniciais do monograma: duas letras, e não mais que isso. */
export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((p) => p.length > 2);
  return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase() || nome.slice(0, 2).toUpperCase();
}

/**
 * *"há 3 dias"*, *"ontem"*, *"hoje"*.
 *
 * ⚠️ **Nulo devolve nulo, e quem chama decide o que escrever**: *"nunca
 * contatada"* e *"nenhuma movimentação"* dizem coisas diferentes, e um traço
 * genérico no lugar das duas apagaria a diferença.
 *
 * ⚠️ A conta é em dia de calendário, não em 24 horas: *"ontem às 23h"* é ontem,
 * mesmo que tenham passado duas horas.
 */
export function quandoFoi(iso: string | null): string | null {
  if (!iso) return null;
  const dia = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const dias = Math.round((dia(new Date()) - dia(new Date(iso))) / 86_400_000);
  if (dias <= 0) return 'hoje';
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;
  if (dias < 60) return 'há um mês';
  return `há ${Math.floor(dias / 30)} meses`;
}

/**
 * ⭐⭐ **Quando o próximo passo VENCE, em palavras — 21/09/2026.**
 *
 * ⚠️ **É o espelho de `quandoFoi`, olhando para a frente.** Ali a pergunta é
 * *"quanto tempo faz?"*; aqui é *"quanto falta?"* — e o que interessa a quem
 * abre a aba de manhã é **hoje, ontem e amanhã**, não a data cheia.
 *
 * ⛔ **Devolve também se está atrasado**, porque quem desenha a linha precisa
 * saber sem recalcular a conta.
 */
export function quandoVence(iso: string | null): { texto: string; atrasado: boolean } | null {
  if (!iso) return null;
  const dia = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  const dias = Math.round((dia(new Date(iso)) - dia(new Date())) / 86_400_000);
  if (dias === 0) return { texto: 'hoje', atrasado: false };
  if (dias === 1) return { texto: 'amanhã', atrasado: false };
  if (dias === -1) return { texto: 'ontem', atrasado: true };
  if (dias < 0) return { texto: `há ${-dias} dias`, atrasado: true };
  if (dias < 30) return { texto: `em ${dias} dias`, atrasado: false };
  return { texto: 'em mais de um mês', atrasado: false };
}

/**
 * ⭐⭐ **O que a empresa do funil leva para o cadastro de CLIENTE.**
 *
 * ⛔ **A razão social não vai junto, e isso é decisão, não esquecimento.** O
 * funil guarda o nome comercial, que é o que a fonte publica; a razão social só
 * a Receita tem. Como o cadastro preenche **só o vazio**, mandar o nome
 * comercial para `nome` travaria o campo — e contrato, proposta e relatório
 * sairiam com o apelido no lugar do nome que a lei reconhece. Ele vai para
 * **Nome fantasia**, que é exatamente o que ele é.
 *
 * ⚠️ **WhatsApp na frente do telefone:** é o número por onde a conversa já
 * estava acontecendo.
 */
export function oQueOFunilLeva(p: Prospeccao): Partial<CamposDoCliente> {
  const principal = p.contatos.find((c) => c.principal) ?? p.contatos[0];
  return {
    tipo: 'pj',
    /* ⭐ O documento, quando o levantamento achou: o cadastro já nasce com ele
       e a Receita completa o resto sozinha. */
    ...(p.documento.length === 14 ? { documento: p.documento } : {}),
    nomeFantasia: p.nome,
    origem: 'PROSPECÇÃO COMERCIAL',
    site: p.site,
    cidade: p.cidade,
    uf: p.uf,
    email: principal?.email ?? '',
    telefone: principal?.whatsapp || principal?.telefone || '',
    representanteNome: principal?.nome ?? '',
    representanteCargo: principal?.cargo ?? '',
  };
}
